/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    // console.log(selectedOptions)

    const selectedVariant = JSON.parse(this.form.dataset.variants).find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    console.log(selectedVariant)

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)

    const accessoryCheckboxes = this.form.querySelectorAll('.accessory-checkbox:checked')

    if (accessoryCheckboxes.length > 0) {
      const items = []

      // Main product
      const mainId = this.form.querySelector('[name="id"]').value
      const mainQty = this.form.querySelector('[name="quantity"]')?.value || 1
      const mainItem = { id: mainId, quantity: parseInt(mainQty) }

      const propertyInputs = this.form.querySelectorAll('[name^="properties["]')
      if (propertyInputs.length > 0) {
        mainItem.properties = {}
        propertyInputs.forEach(input => {
          const name = input.name.match(/properties\[(.*?)\]/)[1]
          mainItem.properties[name] = input.value
        })
      }

      const sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value
      if (sellingPlan) mainItem.selling_plan = sellingPlan

      items.push(mainItem)

      // Accessories
      accessoryCheckboxes.forEach(chk => {
        items.push({
          id: chk.value,
          quantity: 1
        })
      })

      await document.querySelector('cart-container').add({ items })
    } else {
      await document.querySelector('cart-container').add(formData)
    }

    this.atcBtn.classList.remove('loading')
    this.atcBtn.disabled = false
    this.atcBtn.setAttribute('aria-busy', 'false')
  }

  onClickBuyBtn () {
    this.buyBtn.classList.add('loading')
    const variantId = this.form.querySelector('[name="id"]').value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
    window.location.href = `/cart/${variantId}:${qty}`
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }
    return options
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)

const initProductPageMotion = (root = document) => {
  if (!document.body.classList.contains('page-type-product')) return
  if (!window.gsap || !window.ScrollTrigger) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  const scope = root instanceof Element ? root : document
  const gsap = window.gsap
  gsap.registerPlugin(window.ScrollTrigger)

  const selectAll = (selector) => {
    const elements = []
    if (scope.matches?.(selector)) elements.push(scope)
    scope.querySelectorAll?.(selector).forEach((element) => elements.push(element))
    return elements
  }

  const reveal = (selector, fromVars = {}, options = {}) => {
    selectAll(selector).forEach((element) => {
      if (element.dataset.productMotionInit === 'true') return
      if (options.skipStaggerChildren && element.closest('[data-product-stagger]') !== element && element.closest('[data-product-stagger]')) return
      element.dataset.productMotionInit = 'true'

      gsap.fromTo(element, {
        autoAlpha: 0,
        y: 28,
        ...fromVars
      }, {
        autoAlpha: 1,
        x: 0,
        y: 0,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 86%',
          once: true
        }
      })
    })
  }

  reveal('[data-product-reveal]', {}, { skipStaggerChildren: true })
  reveal('.page-type-product #main > .shopify-section:not(:first-child)', { y: 40 })

  selectAll('[data-product-stagger]').forEach((container) => {
    if (container.dataset.productStaggerInit === 'true') return
    container.dataset.productStaggerInit = 'true'

    const items = container.querySelectorAll('[data-product-stagger-item], [data-product-reveal]')

    if (!items.length) return

    gsap.fromTo(items, {
      autoAlpha: 0,
      x: 30
    }, {
      autoAlpha: 1,
      x: 0,
      duration: 0.75,
      ease: 'power3.out',
      stagger: 0.08,
      scrollTrigger: {
        trigger: container,
        start: 'top 84%',
        once: true
      }
    })
  })

  if (!window.matchMedia('(min-width: 900px)').matches) return

  selectAll('[data-product-parallax]').forEach((element) => {
    if (element.dataset.productParallaxInit === 'true') return
    element.dataset.productParallaxInit = 'true'

    gsap.to(element, {
      yPercent: element.closest('.product-media-gallery') ? 5 : -7,
      ease: 'none',
      scrollTrigger: {
        trigger: element.closest('[data-product-section]') || element.closest('.shopify-section') || element.closest('section') || element,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    })
  })
}

const initProductComponentInteractions = (root = document) => {
  if (!document.body.classList.contains('page-type-product')) return

  const scope = root instanceof Element ? root : document
  const gsap = window.gsap

  const selectAll = (selector) => {
    const elements = []
    if (scope.matches?.(selector)) elements.push(scope)
    scope.querySelectorAll?.(selector).forEach((element) => elements.push(element))
    return elements
  }

  selectAll('[data-product-media-tilt]').forEach((wrapper) => {
    if (wrapper.dataset.productTiltInit === 'true') return
    wrapper.dataset.productTiltInit = 'true'

    wrapper.addEventListener('pointermove', (event) => {
      if (!gsap || !window.matchMedia('(pointer: fine)').matches) return

      const rect = wrapper.getBoundingClientRect()
      const x = (event.clientX - rect.left) / rect.width - 0.5
      const y = (event.clientY - rect.top) / rect.height - 0.5

      gsap.to(wrapper, {
        rotateX: y * -3,
        rotateY: x * 4,
        scale: 1.012,
        transformPerspective: 900,
        transformOrigin: 'center',
        duration: 0.35,
        ease: 'power2.out'
      })
    })

    wrapper.addEventListener('pointerleave', () => {
      if (!gsap) return

      gsap.to(wrapper, {
        rotateX: 0,
        rotateY: 0,
        scale: 1,
        duration: 0.55,
        ease: 'power3.out'
      })
    })
  })

  selectAll('[data-product-interactive]').forEach((element) => {
    if (element.dataset.productHoverInit === 'true') return
    element.dataset.productHoverInit = 'true'

    element.addEventListener('pointerenter', () => {
      if (!gsap || element.matches('[disabled], [aria-disabled="true"]')) return

      gsap.to(element, {
        y: element.matches('.btn, label, select') ? -1 : -4,
        scale: element.matches('.btn, label, select') ? 1.025 : 1.018,
        duration: 0.24,
        ease: 'power2.out'
      })
    })

    element.addEventListener('pointerleave', () => {
      if (!gsap) return

      gsap.to(element, {
        y: 0,
        scale: 1,
        duration: 0.32,
        ease: 'power3.out'
      })
    })

    element.addEventListener('pointerdown', () => {
      if (!gsap || element.matches('[disabled], [aria-disabled="true"]')) return

      gsap.to(element, {
        scale: 0.98,
        duration: 0.08,
        ease: 'power2.out'
      })
    })

    element.addEventListener('pointerup', () => {
      if (!gsap || element.matches('[disabled], [aria-disabled="true"]')) return

      gsap.to(element, {
        scale: 1.018,
        duration: 0.18,
        ease: 'back.out(2)'
      })
    })
  })

  selectAll('[data-product-accessories]').forEach((wrapper) => {
    if (wrapper.dataset.productAccessoryInit === 'true') return
    wrapper.dataset.productAccessoryInit = 'true'

    const syncCheckedState = () => {
      wrapper.querySelectorAll('.accessory-item').forEach((item) => {
        const checkbox = item.querySelector('.accessory-checkbox')
        item.classList.toggle('is-selected', Boolean(checkbox?.checked))
      })
    }

    wrapper.addEventListener('change', (event) => {
      if (!event.target.matches('.accessory-checkbox')) return
      syncCheckedState()

      const item = event.target.closest('.accessory-item')
      if (!item || !gsap) return

      gsap.fromTo(item, {
        scale: 0.98
      }, {
        scale: 1,
        duration: 0.28,
        ease: 'back.out(2.1)'
      })
    })

    syncCheckedState()
  })

  if (window.productVariantInteractionInit !== true) {
    window.productVariantInteractionInit = true

    window.addEventListener('kt.product.variant.change', (event) => {
      if (!event.detail || !window.gsap) return

      const gallery = document.querySelector('[data-product-media-tilt]')
      if (!gallery) return

      window.gsap.fromTo(gallery, {
        autoAlpha: 0.82,
        scale: 0.99
      }, {
        autoAlpha: 1,
        scale: 1,
        duration: 0.34,
        ease: 'power2.out'
      })
    }, { once: false })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initProductPageMotion()
    initProductComponentInteractions()
  })
} else {
  initProductPageMotion()
  initProductComponentInteractions()
}

document.addEventListener('shopify:section:load', (event) => {
  initProductPageMotion(event.target || document)
  initProductComponentInteractions(event.target || document)
})
